import { useCallback, useEffect, useMemo, useState } from 'react'
import { LuGlobe, LuShieldAlert, LuUser, LuUsers, LuX } from 'react-icons/lu'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import FormControlLabel from '@mui/material/FormControlLabel'
import MenuItem from '@mui/material/MenuItem'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import Modal from './Modal'
import DeleteConfirm from './DeleteConfirm'
import UserLabel from './UserLabel'
import useOrganizations from '../models/useOrganizations'
import useServices from '../models/useServices'
import { getUserId } from '../lib/workos'
import { ToolCallError } from '../lib/tools'
import { userTextWithEmail } from '../utilities/userLabel'
import { besideFieldRow } from '../utilities/formAlign'
import {
  closeServiceToOrg,
  decideNodeAccess,
  grantNodeAccess,
  readNodeAccess,
  readOrgRoster,
  readMyAccess,
  readServiceVisitors,
  removeServiceVisitor,
  requestNodeAccess,
  revokeNodeAccess,
  setNodeAccess,
} from '../lib/nodeAccess'
import {
  NODE_SETTING_HELP,
  NODE_SETTING_LABEL,
  PRIVATE_LABEL,
  REVOKE_WARNING,
  ROOT_WARNING,
  canUseSummary,
  grantCandidates,
  offerNodeAsk,
  groupRows,
  panelRows,
  presumeController,
  requestSummary,
  revokeFollowUp,
  revokeTerminalLabel,
  rootGrantBlocked,
  nodeSettingsFor,
  sharedReason,
  sharedStatus,
  wayLabel,
  type AccessLevel,
  type MyAccess,
  type NodeAccessView,
  type NodeSetting,
  type OrgMemberLike,
  type PersonRow,
  type RevokeTerminal,
  type ShareList,
  type Way,
} from '../utilities/nodeAccess'

type Props = {
  node: NodeRecord
}

const messageOf = (error: unknown): string => (error instanceof Error ? error.message : String(error))

/**
 * The node's ONE access panel (router PERMISSIONS.md § 4.1, D-26, app#375): who can run on this node
 * and how. The shared setting (Follow the network, or Private (you and org admins)), the access list
 * (root and use apart), and the visitors of every service on the node, each person ONCE with every
 * way they reach it.
 *
 * Only the node's CONTROLLERS see and edit it: its owner, and the Owners and Admins of the node's
 * org. Everyone else sees one button: ask to use the node.
 *
 * ⚠ `presumeController` only picks which view to TRY. The router decides: a read that comes back
 * `forbidden` shows the request view, so the page never shows a list the reader may not change.
 */
export default function NodeAccess({ node }: Readonly<Props>) {
  const organizations = useOrganizations(s => s.organizations)
  const presumed = presumeController(organizations, node, getUserId())
  const [forbidden, setForbidden] = useState(false)
  // Stable, because the panel's read depends on it: a new function each render would re-read the
  // list on every render.
  const onForbidden = useCallback(() => setForbidden(true), [])

  if (!presumed || forbidden) return <RequestAccess nodeId={node.id} nodeName={node.name} />
  return <AccessPanel node={node} onForbidden={onForbidden} />
}

function RequestAccess({ nodeId, nodeName }: Readonly<{ nodeId: string; nodeName: string }>) {
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState('')
  const [error, setError] = useState('')
  // What the reader holds on this node (app#423): undefined until the read answers, null when it
  // cannot say. A member who can use the node already is not offered the ask.
  const [myAccess, setMyAccess] = useState<MyAccess | null | undefined>(undefined)

  useEffect(() => {
    let live = true
    setMyAccess(undefined)
    readMyAccess({ nodeName })
      .then(({ nodes }) => {
        if (live) setMyAccess(nodes.find(n => n.nodeId === nodeId)?.myAccess ?? null)
      })
      .catch(e => {
        console.warn('network_directory my_access', nodeId, e)
        if (live) setMyAccess(null)
      })
    return () => {
      live = false
    }
  }, [nodeId, nodeName])

  const ask = async () => {
    setBusy(true)
    setError('')
    try {
      const answer = await requestNodeAccess(nodeId)
      setResult(requestSummary(answer.requested, answer.skipped.length))
    } catch (e) {
      setError(messageOf(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Stack sx={{ gap: 1, alignItems: 'flex-start' }}>
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        The node owner or an org owner or admin decides who can use this node: run commands and read and write
        files.
      </Typography>
      {myAccess !== undefined && !offerNodeAsk(myAccess) && (
        <Typography variant="body2">{canUseSummary(myAccess)}</Typography>
      )}
      {myAccess !== undefined && offerNodeAsk(myAccess) && !result && (
        <Button variant="outlined" size="small" disabled={busy} onClick={ask}>
          Ask to use this node
        </Button>
      )}
      {result && <Alert severity="info">{result}</Alert>}
      {error && <Alert severity="error">{error}</Alert>}
    </Stack>
  )
}

function AccessPanel({ node, onForbidden }: Readonly<{ node: NodeRecord; onForbidden: () => void }>) {
  const activeOrgId = useOrganizations(s => s.activeOrgId)
  // The node's org roster, read for this panel alone. null: not read, or the reader may not list it.
  const [roster, setRoster] = useState<OrgMemberLike[] | null>(null)
  const members = useMemo(() => roster ?? [], [roster])
  const allServices = useServices(s => s.services)
  const services = useMemo(() => allServices.filter(s => s.nodeId === node.id), [allServices, node.id])
  const [view, setView] = useState<NodeAccessView | null>(null)
  const [loadError, setLoadError] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [followUp, setFollowUp] = useState<{ lines: string[]; terminals: RevokeTerminal[] } | null>(null)
  // The router's notes on the last change: what it did beyond what was asked (the node opened or
  // closed, an allow-root that is not known).
  const [changeNotes, setChangeNotes] = useState<string[]>([])
  // Each service's visitor list, read from the router (service_share list): the addresses, named
  // (router#1841). A service whose read failed keeps the store's fields.
  const [shareLists, setShareLists] = useState<Record<string, ShareList | undefined>>({})
  // Keyed on each service's list as the store holds it, so a change the store receives by realtime
  // reads the router's list again.
  const shareKey = services
    .filter(s => !s.deletedAt)
    .map(s => `${s.id}:${(s.authorizedEmails ?? []).join('|')}:${s.allOrgMembers === true}`)
    .join(',')
  // The members come from the node's OWN org (its tenant), never the org of its network (§ 2).
  const tenant = node.ownerOrgId ?? activeOrgId

  const load = useCallback(async () => {
    try {
      setView(await readNodeAccess(node.id))
      setLoadError('')
    } catch (e) {
      if (e instanceof ToolCallError && e.code === 'forbidden') onForbidden()
      else setLoadError(messageOf(e))
    }
  }, [node.id, onForbidden])

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => {
    let live = true
    setRoster(null)
    if (tenant)
      void readOrgRoster(tenant).then(list => {
        if (live) setRoster(list)
      })
    return () => {
      live = false
    }
  }, [tenant])

  const loadShares = useCallback(async (ids: string[]) => {
    const answers = await Promise.all(
      ids.map(async id => {
        try {
          return [id, (await readServiceVisitors(id)) ?? undefined] as const
        } catch (e) {
          console.warn('service_share list', id, e)
          return [id, undefined] as const
        }
      })
    )
    setShareLists(current => ({ ...current, ...Object.fromEntries(answers) }))
  }, [])

  useEffect(() => {
    if (shareKey) void loadShares(shareKey.split(',').map(part => part.split(':')[0]))
  }, [shareKey, loadShares])

  /**
   * Runs one change, then reads the list again: the router's answer is the state, not ours. Returns
   * true when the change succeeded.
   */
  const run = async (change: () => Promise<string[] | void>): Promise<boolean> => {
    setBusy(true)
    setError('')
    setChangeNotes([])
    try {
      setChangeNotes((await change()) ?? [])
      return true
    } catch (e) {
      setError(messageOf(e))
      return false
    } finally {
      await load()
      setBusy(false)
    }
  }

  const revoke = (row: PersonRow) =>
    run(async () => {
      const result = await revokeNodeAccess(node.id, row.userId || row.email || '')
      const lines = revokeFollowUp(result)
      setFollowUp(lines.length ? { lines, terminals: result.terminals } : null)
    })

  const removeVisitor = (way: Way) => {
    if (way.kind !== 'visitor') return
    void run(async () => {
      if (way.via === 'org') await closeServiceToOrg(way.serviceId)
      else if (way.entry) await removeServiceVisitor(way.serviceId, way.entry)
      await loadShares([way.serviceId])
    })
  }

  // A note the list read already shows next to the setting is not repeated.
  const freshNotes = changeNotes.filter(note => !view?.notes.includes(note))

  if (loadError) return <Alert severity="error">{loadError}</Alert>
  if (!view)
    return (
      <Box>
        <CircularProgress size={18} />
      </Box>
    )

  const groups = groupRows(panelRows(view, services, members, shareLists))
  const rootBlocked = rootGrantBlocked(view.allowRoot)

  return (
    <Stack sx={{ gap: 2 }}>
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        Who can run on this node, and how. You, the node owner and the org owners and admins always can, and are
        not listed.
      </Typography>

      {/* The shared setting (§ 4.1, D-20): follow the network, or Private (you and org admins). A
          node in no network can also be set to Org (router#2137). */}
      <Box>
        <Stack direction="row" sx={{ alignItems: 'center', gap: 1, marginBottom: 0.5, flexWrap: 'wrap' }}>
          <Typography variant="subtitle2">Now:</Typography>
          <Chip
            size="small"
            variant="outlined"
            color={view.sharedWithOrg ? 'primary' : 'default'}
            icon={view.sharedWithOrg ? <LuUsers /> : <LuUser />}
            label={sharedStatus(view)}
          />
        </Stack>
        <Typography variant="caption" component="div" sx={{ color: 'text.secondary', marginBottom: 1 }}>
          {sharedReason(view)}
        </Typography>
        <RadioGroup
          value={view.access}
          onChange={event => {
            const next = event.target.value as NodeSetting
            if (next !== view.access) void run(() => setNodeAccess(node.id, next))
          }}
        >
          {nodeSettingsFor(view).map(setting => (
            <FormControlLabel
              key={setting}
              value={setting}
              disabled={busy}
              control={<Radio size="small" color="secondary" sx={{ paddingTop: 0.25 }} />}
              sx={{ alignItems: 'flex-start', marginLeft: 0, marginRight: 0, marginBottom: 0.5 }}
              label={
                <>
                  <Typography variant="body2">{NODE_SETTING_LABEL[setting]}</Typography>
                  <Typography variant="caption" component="div" sx={{ color: 'text.secondary' }}>
                    {NODE_SETTING_HELP[setting]}
                  </Typography>
                </>
              }
            />
          ))}
        </RadioGroup>
      </Box>

      {/* ⚠ Next to the setting (§ 4): on a node that offers only root, every call is root, so a use
          entry or sharing lets nobody do anything. The router writes the sentence. */}
      {view.notes.map(note => (
        <Alert key={note} severity="warning">
          {note}
        </Alert>
      ))}

      <RowGroup
        title="Root"
        caption="Access-list entries at root. Root includes use, and is never given by sharing."
        empty="Nobody is on the list at root."
        rows={groups.root}
        busy={busy}
        onRevoke={revoke}
        onRemoveVisitor={removeVisitor}
      />
      <RowGroup
        title="Use"
        caption="Access-list entries at use. They count whatever the shared setting is."
        empty="Nobody is on the list at use."
        rows={groups.use}
        busy={busy}
        onRevoke={revoke}
        onRemoveVisitor={removeVisitor}
      />
      {groups.visitors.length > 0 && (
        <RowGroup
          title="Service visitors"
          caption="People a service on this node names. A terminal visitor runs a shell on the node."
          rows={groups.visitors}
          busy={busy}
          onRevoke={revoke}
          onRemoveVisitor={removeVisitor}
        />
      )}
      {groups.groups.length > 0 && (
        <RowGroup
          title="Groups"
          rows={groups.groups}
          busy={busy}
          onRevoke={revoke}
          onRemoveVisitor={removeVisitor}
        />
      )}

      <GrantForm
        members={grantCandidates(members, view, node.ownerUserId, 'use')}
        rootMembers={grantCandidates(members, view, node.ownerUserId, 'root')}
        rosterKnown={roster !== null}
        rootBlocked={rootBlocked}
        busy={busy}
        onGrant={(user, level, confirmedRoot) => run(() => grantNodeAccess(node.id, user, level, confirmedRoot))}
      />

      {view.pending.length > 0 && (
        <Box>
          <Typography variant="subtitle2">Requests</Typography>
          {view.pending.map(request => (
            <Stack
              key={request.requestId}
              direction="row"
              sx={{ alignItems: 'center', gap: 1, paddingY: 0.5, flexWrap: 'wrap' }}
            >
              <LuUser />
              <Typography variant="body2" sx={{ flexGrow: 1, wordBreak: 'break-all' }}>
                <UserLabel user={{ name: request.name, email: request.email, id: request.userId }} sx={{ wordBreak: 'inherit' }} />{' '}
                asks to use this node
              </Typography>
              <Button
                size="small"
                variant="outlined"
                disabled={busy}
                onClick={() => run(() => decideNodeAccess(request.requestId, 'grant'))}
              >
                Grant use
              </Button>
              <Button
                size="small"
                variant="text"
                disabled={busy}
                onClick={() => run(() => decideNodeAccess(request.requestId, 'refuse'))}
              >
                Refuse
              </Button>
            </Stack>
          ))}
        </Box>
      )}

      {freshNotes.length > 0 && (
        <Alert severity="info" onClose={() => setChangeNotes([])}>
          {freshNotes.map(note => (
            <Typography key={note} variant="body2">
              {note}
            </Typography>
          ))}
        </Alert>
      )}
      {followUp && (
        <Alert severity="info" onClose={() => setFollowUp(null)}>
          {followUp.lines.map(line => (
            <Typography key={line} variant="body2">
              {line}
            </Typography>
          ))}
          {followUp.terminals.length > 0 && (
            <Box component="ul" sx={{ margin: 0, paddingLeft: 2.5 }}>
              {followUp.terminals.map(t => (
                <li key={t.serviceId}>
                  <Typography variant="body2">{revokeTerminalLabel(t)}</Typography>
                </li>
              ))}
            </Box>
          )}
        </Alert>
      )}
      {error && <Alert severity="error">{error}</Alert>}
    </Stack>
  )
}

type RowGroupProps = {
  title: string
  caption?: string
  empty?: string
  rows: PersonRow[]
  busy: boolean
  onRevoke: (row: PersonRow) => void
  onRemoveVisitor: (way: Way) => void
}

const rowIcon = (row: PersonRow) => {
  if (row.kind === 'org' || row.kind === 'pattern') return <LuUsers />
  if (row.kind === 'public') return <LuGlobe />
  return row.ways.some(w => w.kind === 'root') ? <LuShieldAlert /> : <LuUser />
}

function RowGroup({ title, caption, empty, rows, busy, onRevoke, onRemoveVisitor }: Readonly<RowGroupProps>) {
  return (
    <Box>
      <Typography variant="subtitle2">{title}</Typography>
      {caption && (
        <Typography variant="caption" component="div" sx={{ color: 'text.secondary' }}>
          {caption}
        </Typography>
      )}
      {rows.length === 0 && empty && (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {empty}
        </Typography>
      )}
      {rows.map(row => (
        <PersonLine key={row.key} row={row} busy={busy} onRevoke={onRevoke} onRemoveVisitor={onRemoveVisitor} />
      ))}
    </Box>
  )
}

type PersonLineProps = {
  row: PersonRow
  busy: boolean
  onRevoke: (row: PersonRow) => void
  onRemoveVisitor: (way: Way) => void
}

/** One person (or group), once, with every way they reach the node and a remove for each way. */
function PersonLine({ row, busy, onRevoke, onRemoveVisitor }: Readonly<PersonLineProps>) {
  const onList = row.ways.some(w => w.kind === 'use' || w.kind === 'root')
  const who = row.label
  // A person's name, with the email on hover; the email alone otherwise (app#401).
  const person = row.kind === 'person' ? { name: row.name, email: row.email || row.label } : null
  return (
    <Box sx={{ paddingY: 0.5 }}>
      <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
        {rowIcon(row)}
        {person ? (
          <UserLabel user={person} sx={{ flexGrow: 1, wordBreak: 'break-all' }} />
        ) : (
          <Typography variant="body2" sx={{ flexGrow: 1, wordBreak: 'break-all' }}>
            {who}
          </Typography>
        )}
        {onList && (
          <DeleteConfirm
            title={`Remove ${person ? userTextWithEmail(person, who) : who} from the access list?`}
            action="Remove"
            message={REVOKE_WARNING}
            disabled={busy}
            onConfirm={() => onRevoke(row)}
          >
            <Button size="small" variant="text" color="error" disabled={busy} startIcon={<LuX />}>
              Remove
            </Button>
          </DeleteConfirm>
        )}
      </Stack>
      <Stack direction="row" sx={{ gap: 0.5, flexWrap: 'wrap', paddingLeft: 3 }}>
        {row.ways.map(way => {
          const label = wayLabel(way)
          // D-5: a root entry under allow-root false is KEPT but inert, and says so.
          const blocked = way.kind === 'root' && way.blocked
          // Only a way named on THIS row can be removed from it: a pattern's way is removed on the
          // pattern's row, and the org's on the org's row.
          const removable =
            way.kind === 'visitor' && (way.via === 'named' || (way.via === 'org' && row.kind === 'org'))
          return (
            <Chip
              key={`${label}-${way.kind === 'visitor' ? way.serviceId : ''}`}
              size="small"
              variant="outlined"
              color={blocked ? 'warning' : way.kind === 'root' || (way.kind === 'visitor' && way.rootTerminal) ? 'error' : 'default'}
              label={label}
              title={
                blocked
                  ? 'This node does not allow root (allow-root is off at the machine). The entry works again if the node owner allows root.'
                  : way.kind === 'shared'
                    ? `Set the node to ${PRIVATE_LABEL} to stop this.`
                    : undefined
              }
              disabled={busy}
              onDelete={removable ? () => onRemoveVisitor(way) : undefined}
            />
          )
        })}
      </Stack>
    </Box>
  )
}

type Candidate = { userId: string; email: string; name?: string }

type GrantFormProps = {
  members: Candidate[]
  rootMembers: Candidate[]
  /** false: the org's member list could not be read (only an Owner or Admin lists it), so the form takes an email. */
  rosterKnown: boolean
  rootBlocked: string | null
  busy: boolean
  /**
   * Resolves true when the grant succeeded. `confirmedRoot` is true only when the reader ticked the
   * root warning and pressed Give root: it becomes `confirm_root`.
   */
  onGrant: (user: string, level: AccessLevel, confirmedRoot: boolean) => Promise<boolean>
}

export function GrantForm({ members, rootMembers, rosterKnown, rootBlocked, busy, onGrant }: Readonly<GrantFormProps>) {
  const [level, setLevel] = useState<AccessLevel>('use')
  const [user, setUser] = useState('')
  const [confirming, setConfirming] = useState(false)
  const [understood, setUnderstood] = useState(false)
  const candidates = level === 'root' ? rootMembers : members
  const picked = rosterKnown ? candidates.find(m => m.userId === user) : undefined
  // Without the roster, the router resolves the typed email against the node's org.
  const target = rosterKnown ? picked?.userId : user.trim()
  const targetLabel = rosterKnown ? (picked ? userTextWithEmail(picked) : '') : user.trim()

  const grant = async (confirmedRoot: boolean) => {
    if (!target) return
    // A refused grant keeps the form as it was, so the reader can read the refusal and try again.
    if (!(await onGrant(target, level, confirmedRoot))) return
    setUser('')
    setLevel('use')
  }

  const closeConfirm = () => {
    setConfirming(false)
    setUnderstood(false)
  }

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ marginBottom: 1 }}>
        Add a member to the access list
      </Typography>
      {/* ⚠ Tops, not centres: the fields' labels sit above their boxes and the Member field can
          carry a helper line below, so a centred row put Add 12px above the boxes and the Level
          box 9px below the Member box. `besideFieldRow` moves Add down to the boxes (formAlign.ts). */}
      <Stack direction={{ xs: 'column', sm: 'row' }} sx={{ gap: 1, alignItems: { sm: 'flex-start' } }}>
        {rosterKnown ? (
          <TextField
            select
            size="small"
            label="Member"
            value={picked ? user : ''}
            onChange={event => setUser(event.target.value)}
            sx={{ minWidth: 220, flexGrow: 1 }}
            disabled={busy || candidates.length === 0}
            helperText={candidates.length === 0 ? 'No other member of the org to add.' : undefined}
          >
            {candidates.map(m => (
              <MenuItem key={m.userId} value={m.userId}>
                {userTextWithEmail(m)}
              </MenuItem>
            ))}
          </TextField>
        ) : (
          <TextField
            size="small"
            label="Member's email"
            value={user}
            onChange={event => setUser(event.target.value)}
            sx={{ minWidth: 220, flexGrow: 1 }}
            disabled={busy}
            helperText="A member of this node's org."
          />
        )}
        <TextField
          select
          size="small"
          label="Level"
          value={level}
          onChange={event => setLevel(event.target.value as AccessLevel)}
          sx={{ minWidth: 110 }}
          disabled={busy}
        >
          <MenuItem value="use">use</MenuItem>
          <MenuItem value="root" disabled={!!rootBlocked}>
            root
          </MenuItem>
        </TextField>
        <Button
          variant="outlined"
          size="small"
          sx={besideFieldRow}
          disabled={busy || !target}
          onClick={() => (level === 'root' ? setConfirming(true) : void grant(false))}
        >
          Add
        </Button>
      </Stack>
      {rootBlocked && (
        <Typography variant="caption" component="div" sx={{ color: 'text.secondary', marginTop: 0.5 }}>
          {rootBlocked}
        </Typography>
      )}

      {/* ⚠⚠ A root grant is NEVER silent (§ 4.1, D-3): the plain warning, and an explicit
          confirmation before the app sends `confirm_root: true`. */}
      <Modal open={confirming} onClose={closeConfirm} color="error.main">
        <DialogTitle variant="subtitle1" color="error">
          Give root to {targetLabel || 'this member'}?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" component="div" sx={{ marginBottom: 2 }}>
            {ROOT_WARNING}
          </Typography>
          <FormControlLabel
            control={<Checkbox checked={understood} onChange={e => setUnderstood(e.target.checked)} />}
            label="I understand that root gives everything on this machine."
          />
        </DialogContent>
        <DialogActions>
          <Button variant="text" sx={{ color: 'GrayText' }} onClick={closeConfirm}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={!understood || busy}
            onClick={() => {
              closeConfirm()
              void grant(understood)
            }}
          >
            Give root
          </Button>
        </DialogActions>
      </Modal>
    </Box>
  )
}
